<template>
  <main
    class="relative min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-10 overflow-hidden"
  >
    <!-- Background Decorative Elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-600/20 rounded-full blur-3xl animate-pulse"
      ></div>
      <div
        class="absolute top-1/2 -left-40 w-96 h-96 bg-gradient-to-br from-emerald-400/15 to-teal-600/15 rounded-full blur-3xl animate-pulse"
        style="animation-delay: 2s"
      ></div>
      <div
        class="absolute -bottom-40 right-1/4 w-72 h-72 bg-gradient-to-br from-purple-400/20 to-pink-600/20 rounded-full blur-3xl animate-pulse"
        style="animation-delay: 4s"
      ></div>
    </div>

    <!-- Header Section -->
    <div class="relative z-10 px-6 pt-16 pb-12">
      <div class="text-center mb-16">
        <h1
          class="text-xl md:text-2xl font-extrabold bg-yellow bg-clip-text text-transparent mb-6 animate-fade-in-up"
        >
          Our Group Objectives
        </h1>
        <div
          class="mt-2 w-24 h-1 bg-yellow mx-auto rounded-full opacity-0 animate-fade-in-up"
          style="animation-delay: 0.4s; animation-fill-mode: forwards"
        ></div>
      </div>

      <!-- Objectives Grid -->
      <div
        class="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto lg:px-4 px-2"
      >
        <!-- Objective 1 -->
        <div
          class="cursor-pointer group bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-[1.02] border border-white/50 hover:border-emerald-200 opacity-0 animate-fade-in-up relative overflow-hidden"
          style="animation-delay: 0.1s; animation-fill-mode: forwards"
        >
          <!-- Card Glow Effect -->
          <div
            class="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"
          ></div>

          <!-- Responsive Flex Container -->
          <div
            class="relative z-10 flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <!-- Icon -->
            <div class="flex-shrink-0">
              <div
                class="w-16 h-16 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg group-hover:shadow-emerald-500/50"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
              </div>
            </div>

            <!-- Text Content -->
            <div class="flex-1">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-4 space-y-2 sm:space-y-0"
              >
                <span
                  class="bg-emerald-100 text-emerald-800 text-sm font-bold px-4 py-2 rounded-full group-hover:bg-emerald-200 transition-colors duration-300"
                  >01</span
                >
                <h3
                  class="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors duration-300"
                >
                  Deliver Exceptional Quality & Value
                </h3>
              </div>
              <p
                class="text-gray-600 leading-relaxed text-base sm:text-lg group-hover:text-gray-700 transition-colors duration-300"
              >
                To provide top-tier products and services across all
                subsidiaries, exceeding client expectations by maintaining
                rigorous standards of quality, integrity, and craftsmanship in
                real estate, hospitality, logistics, construction, and beyond.
              </p>
            </div>
          </div>
        </div>

        <!-- Objective 2 -->
        <div
          class="cursor-pointer group bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-[1.02] border border-white/50 hover:border-blue-200 opacity-0 animate-fade-in-up relative overflow-hidden"
          style="animation-delay: 0.2s; animation-fill-mode: forwards"
        >
          <!-- Card Glow Effect -->
          <div
            class="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"
          ></div>

          <!-- Responsive Flex Container -->
          <div
            class="relative z-10 flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <!-- Icon -->
            <div class="flex-shrink-0">
              <div
                class="w-16 h-16 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg group-hover:shadow-blue-500/50"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  ></path>
                </svg>
              </div>
            </div>

            <!-- Text Content -->
            <div class="flex-1">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-4 space-y-2 sm:space-y-0"
              >
                <span
                  class="bg-blue-100 text-blue-800 text-sm font-bold px-4 py-2 rounded-full group-hover:bg-blue-200 transition-colors duration-300"
                  >02</span
                >
                <h3
                  class="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-300"
                >
                  Drive Innovation Across Sectors
                </h3>
              </div>
              <p
                class="text-gray-600 leading-relaxed text-base sm:text-lg group-hover:text-gray-700 transition-colors duration-300"
              >
                To pioneer innovative solutions and leverage cutting-edge
                technologies, setting new industry benchmarks in areas such as
                sustainable housing, efficient logistics, and modern hospitality
                experiences.
              </p>
            </div>
          </div>
        </div>

        <!-- Objective 3 -->
        <div
          class="cursor-pointer group bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-[1.02] border border-white/50 hover:border-purple-200 opacity-0 animate-fade-in-up relative overflow-hidden"
          style="animation-delay: 0.3s; animation-fill-mode: forwards"
        >
          <!-- Card Glow Effect -->
          <div
            class="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"
          ></div>

          <!-- Responsive Flex Container -->
          <div
            class="relative z-10 flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <!-- Icon -->
            <div class="flex-shrink-0">
              <div
                class="w-16 h-16 bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg group-hover:shadow-purple-500/50"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  ></path>
                </svg>
              </div>
            </div>

            <!-- Text Content -->
            <div class="flex-1">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-4 space-y-2 sm:space-y-0"
              >
                <span
                  class="bg-purple-100 text-purple-800 text-sm font-bold px-4 py-2 rounded-full group-hover:bg-purple-200 transition-colors duration-300"
                  >03</span
                >
                <h3
                  class="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors duration-300"
                >
                  Empower Communities
                </h3>
              </div>
              <p
                class="text-gray-600 leading-relaxed text-base sm:text-lg group-hover:text-gray-700 transition-colors duration-300"
              >
                To uplift and strengthen Nigerian and African communities
                through impactful programs, job creation, and infrastructure
                development, with a focus on enhancing quality of life and
                fostering economic growth.
              </p>
            </div>
          </div>
        </div>

        <!-- Objective 4 -->
        <div
          class="cursor-pointer group bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 hover:scale-[1.02] border border-white/50 hover:border-orange-200 opacity-0 animate-fade-in-up relative overflow-hidden"
          style="animation-delay: 0.4s; animation-fill-mode: forwards"
        >
          <!-- Card Glow Effect -->
          <div
            class="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"
          ></div>

          <!-- Responsive Flex Container -->
          <div
            class="relative z-10 flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <!-- Icon -->
            <div class="flex-shrink-0">
              <div
                class="w-16 h-16 bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg group-hover:shadow-orange-500/50"
              >
                <svg
                  class="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>

            <!-- Text Content -->
            <div class="flex-1">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-4 space-y-2 sm:space-y-0"
              >
                <span
                  class="bg-orange-100 text-orange-800 text-sm font-bold px-4 py-2 rounded-full group-hover:bg-orange-200 transition-colors duration-300"
                  >04</span
                >
                <h3
                  class="text-lg sm:text-xl font-bold text-gray-900 group-hover:text-orange-700 transition-colors duration-300"
                >
                  Expand Market Reach and Impact
                </h3>
              </div>
              <p
                class="text-gray-600 leading-relaxed text-base sm:text-lg group-hover:text-gray-700 transition-colors duration-300"
              >
                To grow the presence and influence of Mshel Group subsidiaries
                across Nigeria, Africa, and globally, delivering tailored
                solutions that address local needs and drive sustainable
                progress.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Call to Action Section -->
      <!-- <div
        class="text-center mt-16 opacity-0 animate-fade-in-up"
        style="animation-delay: 0.6s; animation-fill-mode: forwards"
      >
        <a
          href="https://mshelhomes.com/earn-with-us"
          target="_blank"
          role="button"
          class="inline-flex items-center space-x-4 bg-white/80 backdrop-blur-sm rounded-full px-8 py-4 shadow-lg border border-white/60 hover:bg-white/90 hover:shadow-xl transition-all duration-300 cursor-pointer group"
        >
          <span class="text-gray-800 font-semibold text-lg"
            >Ready to partner with us?</span
          >
          <svg
            class="w-6 h-6 text-gray-800 group-hover:translate-x-2 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            ></path>
          </svg>
        </a>
      </div> -->
    </div>
  </main>
</template>

<script>
export default {
  name: "GroupObjectives",
};
</script>

<style scoped>
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out;
}

/* Enhanced backdrop blur support */
@supports (backdrop-filter: blur(20px)) {
  .backdrop-blur-xl {
    backdrop-filter: blur(20px);
  }
}

/* Fallback for browsers without backdrop-filter support */
@supports not (backdrop-filter: blur(20px)) {
  .backdrop-blur-xl {
    background-color: rgba(255, 255, 255, 0.9);
  }
}

/* Custom gradient animations */
.bg-gradient-to-br {
  background-size: 400% 400%;
}

/* Enhanced hover effects */
.group:hover .group-hover\:shadow-emerald-500\/50 {
  box-shadow: 0 25px 50px -12px rgba(16, 185, 129, 0.5);
}

.group:hover .group-hover\:shadow-blue-500\/50 {
  box-shadow: 0 25px 50px -12px rgba(59, 130, 246, 0.5);
}

.group:hover .group-hover\:shadow-purple-500\/50 {
  box-shadow: 0 25px 50px -12px rgba(168, 85, 247, 0.5);
}

.group:hover .group-hover\:shadow-orange-500\/50 {
  box-shadow: 0 25px 50px -12px rgba(249, 115, 22, 0.5);
}
</style>
